import React, { useMemo, useRef, useState } from 'react';
import catalogJson from '../../../../SKILLs/pptx/references/styles/styles.json';
import styles from './PptxStylePicker.module.css';
import { buildPptxStyleAnswer } from './pptxStyleAnswer';

/**
 * 对话内的 PPT 风格选择卡。
 *
 * pptx 技能在新建 deck 且用户没点名风格时,用 AskUserQuestion(header "PPT 风格")发问;
 * 这里不走普通答题表单,而是把技能目录 `references/styles/styles.json` 的 24 套风格画成 SVG 缩略卡
 * (按真实配色 token 画迷你样页,不是 AI 生成图),外加密度档、品牌色与"自定义风格"。
 * answers 包含所选主题、用户覆盖项与可执行生成约束；不改变工具协议。
 */

type StyleTokens = { bg: string; ink: string; muted: string; line: string; accent: string; accent2: string; tint: string };
export type PptxStyle = {
  id: string; name: string; en: string; family: string; familyName: string; mood: string[]; scenes: string[];
  density: string; dark: boolean; tokens: StyleTokens; fonts: { title: string; body: string; number: string };
  shape: { radius: number; border: number; shadow: boolean; cardFill: string };
  presentation: { rules: boolean; fillTransparency: number; shadowColor: string; shadowOpacity: number };
};
type Catalog = { families: { id: string; name: string }[]; densities: { id: string; name: string }[]; styles: PptxStyle[] };

const catalog = catalogJson as unknown as Catalog;
const CUSTOM_FAMILY = 'custom';

/** 判断一道 AskUserQuestion 是否是 pptx 技能的风格选择题。 */
export function isPptxStyleQuestion(question: { header?: string; question: string; options?: { label: string }[] }): boolean {
  return /ppt\s*风格|pptx[-_ ]?style|style_picker/i.test(`${question.header ?? ''} ${question.question}`)
    && recommendedIds(question.options ?? []).length > 0;
}

/** 从模型给的 4 个选项里认出推荐的风格编号(label 形如 "A3 白底黑金尽调")。 */
function recommendedIds(options: { label: string }[]): string[] {
  const ids = new Set(catalog.styles.map(s => s.id));
  return options.map(o => (o.label.match(/\b([A-F][1-4])\b/i)?.[1] ?? '').toUpperCase()).filter(id => ids.has(id));
}

const hex = (value: string) => `#${value.replace(/^#/, '')}`;
const isValidHex = (value: string) => /^#?[0-9a-fA-F]{6}$/.test(value.trim());

/** Theme illustration, not a promise that every slide has this composition. */
export const PptxStyleThumb: React.FC<{ style: PptxStyle; brand?: string; className?: string }> = ({ style, brand, className }) => {
  const t = style.tokens;
  const accent = brand && isValidHex(brand) ? hex(brand) : hex(t.accent);
  const r = Math.min(6, style.shape.radius / 3);
  const cardFill = style.shape.cardFill === 'none' ? 'none' : hex(t.tint);
  const cardStroke = style.shape.border > 0 ? hex(t.line) : 'none';
  const shadow = style.shape.shadow ? `url(#shadow-${style.id})` : undefined;
  const bars = [0.42, 0.55, 0.68, 0.86, 1];
  const fam = style.family;
  return (
    <svg viewBox="0 0 160 90" width="100%" height="100%" className={className} role="img" aria-label={`${style.name} 样页`}>
      <defs>
        <filter id={`shadow-${style.id}`} x="-10%" y="-10%" width="130%" height="130%">
          <feDropShadow dx="1" dy="1.5" stdDeviation="1.6" floodColor={hex(style.presentation.shadowColor)} floodOpacity={style.presentation.shadowOpacity} />
        </filter>
      </defs>
      <rect width="160" height="90" fill={hex(t.bg)} />
      {fam === 'F' && style.id === 'F1' ? (
        <>
          <text x="12" y="46" fontSize="30" fontWeight="900" fill={hex(t.ink)} fontFamily="Arial Black, Arial, sans-serif">420</text>
          <rect x="12" y="60" width="70" height="3" fill={hex(t.muted)} opacity="0.8" />
          <rect x="12" y="67" width="52" height="3" fill={hex(t.muted)} opacity="0.5" />
          {bars.map((h, i) => <rect key={i} x={100 + i * 10} y={70 - h * 34} width="6" height={h * 34} fill={i === 3 ? accent : hex(t.ink)} opacity={i === 3 ? 1 : 0.85} />)}
        </>
      ) : (
        <>
          {/* 眉题 + 页码 + 发丝线 */}
          <rect x="10" y="8" width="28" height="2" fill={hex(t.muted)} />
          <rect x="146" y="8" width="5" height="2" fill={hex(t.muted)} />
          {style.presentation.rules && <rect x="10" y="13" width="140" height="0.6" fill={hex(t.line)} />}
          {/* 行动标题 */}
          <rect x="10" y="17" width={fam === 'B' ? 70 : 96} height={fam === 'B' ? 5 : 4} fill={hex(t.ink)} />
          {/* 左栏:卡片 + 柱图 / 折线 */}
          <rect x="10" y="27" width="80" height="44" rx={r} fill={cardFill} fillOpacity={1 - style.presentation.fillTransparency / 100} stroke={cardStroke} strokeWidth="0.6" filter={shadow} />
          {[0, 1, 2].map(i => <rect key={i} x="14" y={38 + i * 11} width="72" height="0.5" fill={hex(t.line)} />)}
          {fam === 'B' ? (
            <polyline points="16,58 30,52 44,54 58,44 72,40 84,33" fill="none" stroke={hex(t.ink)} strokeWidth="1.4" />
          ) : bars.map((h, i) => (
            <rect key={i} x={16 + i * 14} y={66 - h * 30} width="8" height={h * 30} fill={i === 3 ? accent : hex(t.ink)} opacity={i === 3 ? 1 : i === 4 ? 0.35 : 0.75} />
          ))}
          {fam === 'B' && <circle cx="58" cy="44" r="2.2" fill={accent} />}
          {/* 右栏:表格 / 矩阵 / KPI */}
          <rect x="96" y="27" width="54" height="44" rx={r} fill={cardFill} fillOpacity={1 - style.presentation.fillTransparency / 100} stroke={cardStroke} strokeWidth="0.6" filter={shadow} />
          {fam === 'C' ? (
            <>
              <text x="101" y="40" fontSize="9" fontWeight="700" fill={hex(t.ink)} fontFamily="Arial, sans-serif">52%</text>
              <rect x="101" y="44" width="30" height="1.5" fill={hex(t.muted)} opacity="0.7" />
              {[0, 1, 2].map(i => <rect key={i} x="101" y={52 + i * 6} width={40 - i * 8} height="2" fill={hex(t.muted)} opacity="0.6" />)}
            </>
          ) : (
            <>
              <rect x="100" y="31" width="46" height="5" fill={hex(t.tint)} stroke={hex(t.line)} strokeWidth="0.4" />
              {[0, 1, 2, 3, 4].map(i => (
                <g key={i}>
                  <rect x="100" y={40 + i * 6} width="20" height="1.6" fill={hex(t.ink)} opacity="0.7" />
                  <rect x="126" y={40 + i * 6} width="12" height="1.6" fill={hex(t.ink)} opacity="0.55" />
                  <rect x="100" y={44 + i * 6} width="46" height="0.4" fill={hex(t.line)} />
                </g>
              ))}
            </>
          )}
          {/* 重点框 + 页脚 */}
          <rect x="10" y="75" width="140" height="6" fill={hex(t.tint)} />
          {style.presentation.rules && <rect x="10" y="84" width="140" height="0.5" fill={hex(t.line)} />}
          <rect x="10" y="86" width="30" height="1.2" fill={hex(t.muted)} />
        </>
      )}
    </svg>
  );
};

interface Props {
  question: { question: string; header?: string; options: { label: string; description?: string }[] };
  language?: string;
  onSubmit: (answers: Record<string, string>) => void | Promise<void>;
  onDeny: () => void | Promise<void>;
}

const PptxStylePicker: React.FC<Props> = ({ question, language = 'zh', onSubmit, onDeny }) => {
  const zh = language.startsWith('zh');
  const recommended = useMemo(() => recommendedIds(question.options), [question.options]);
  const hasWebOption = question.options.some(o => /plus-ppt|网页版/i.test(o.label));
  const [family, setFamily] = useState<string>('all');
  const [selected, setSelected] = useState<string>(recommended[0] ?? catalog.styles[0]?.id ?? '');
  const [density, setDensity] = useState<string>('');
  const [brand, setBrand] = useState<string>('');
  const [customText, setCustomText] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState('');

  const current = catalog.styles.find(s => s.id === selected);
  const effectiveDensity = density || current?.density || 'standard';
  const densityName = (id: string) => catalog.densities.find(d => d.id === id)?.name ?? id;
  const visible = useMemo(() => {
    const list = family === 'all' ? catalog.styles : catalog.styles.filter(s => s.family === family);
    // 推荐的排前面
    return [...list].sort((a, b) => Number(recommended.includes(b.id)) - Number(recommended.includes(a.id)));
  }, [family, recommended]);
  const isCustom = selected === CUSTOM_FAMILY;
  const isWeb = selected === 'plus-ppt';
  const canSubmit = isWeb || (isCustom ? customText.trim().length >= 10 : Boolean(current)) && (!brand.trim() || isValidHex(brand));

  /** 复用审批协议；失败保留风格，互斥锁防止快速双击重复提交。 */
  const submit = async (deny = false) => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true); setError('');
    try {
      if (deny) { await onDeny(); return; }
      const brandText = brand.trim() && isValidHex(brand) ? hex(brand.trim()).toUpperCase() : (zh ? '默认' : 'default');
      let answer: string;
      if (isWeb) answer = '网页版(plus-ppt)';
      else if (isCustom) answer = `自定义风格 | 密度:${densityName(effectiveDensity)} | 品牌色:${brandText} | 描述:${customText.trim()}`;
      else answer = buildPptxStyleAnswer(current!.id, effectiveDensity, brand);
      await onSubmit({ [question.question]: answer });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally { submitting.current = false; setBusy(false); }
  };

  return (
    <div className={styles.root} data-pptx-style-picker aria-busy={busy}>
      {/* 内容区独立滚动，抽屉缩小或报错时提交操作仍完整可见。 */}
      <div className={styles.body}>
        <div className={styles.heading}>
          <div className={styles.kicker}>{question.header ?? (zh ? 'PPT 风格' : 'Deck style')}</div>
          <h3>{question.question}</h3>
          <p className={styles.hint}>{zh ? '缩略图展示配色与形状语言；实际版式随内容与密度调整。密度与品牌色可选。' : 'Thumbnails illustrate palette and shape language; layouts adapt to content and density. Brand color is optional.'}</p>
        </div>
        <div className={styles.tabs} role="tablist">
          {[{ id: 'all', name: zh ? '全部' : 'All' }, ...catalog.families, { id: CUSTOM_FAMILY, name: zh ? '其他' : 'Other' }].map(f => (
            <button key={f.id} type="button" role="tab" aria-selected={family === f.id} className={styles.tab} onClick={() => setFamily(f.id)} disabled={busy}>{f.name}</button>
          ))}
        </div>
        <div className={styles.grid} role="listbox" aria-label={zh ? '风格列表' : 'Styles'}>
          {family !== CUSTOM_FAMILY && visible.map(s => {
            const active = selected === s.id;
            return (
              <button key={s.id} type="button" role="option" aria-selected={active} className={`${styles.card} ${active ? styles.cardActive : ''}`} onClick={() => setSelected(s.id)} disabled={busy} title={`${s.scenes.join(' / ')} · ${s.fonts.title} / ${s.fonts.body}`}>
                <div className={styles.thumb}><PptxStyleThumb style={s} brand={brand} /></div>
                {recommended.includes(s.id) && <span className={styles.badge}>{zh ? '推荐' : 'Pick'}</span>}
                <div className={styles.meta}>
                  <div className={styles.nameRow}>
                    <span className={styles.id}>{s.id}</span>
                    <span className={styles.name}>{s.name}</span>
                    <span className={styles.swatches} aria-hidden="true">
                      {[s.tokens.bg, s.tokens.ink, (brand && isValidHex(brand)) ? brand : s.tokens.accent, s.tokens.accent2].map((c, i) => <i key={i} style={{ background: hex(c) }} />)}
                    </span>
                  </div>
                  <div className={styles.mood}>{s.mood.join(' · ')}</div>
                </div>
              </button>
            );
          })}
          {(family === CUSTOM_FAMILY || family === 'all') && (
            <button type="button" role="option" aria-selected={isCustom} className={`${styles.card} ${styles.cardCustom} ${isCustom ? styles.cardActive : ''}`} onClick={() => setSelected(CUSTOM_FAMILY)} disabled={busy}>
              <div className={styles.thumb}><div className={styles.customThumb}>＋</div></div>
              <div className={styles.meta}>
                <div className={styles.nameRow}><span className={styles.name}>{zh ? '自定义风格' : 'Custom style'}</span></div>
                <div className={styles.mood}>{zh ? '粘贴你自己的风格描述' : 'Paste your own style brief'}</div>
              </div>
            </button>
          )}
          {(family === CUSTOM_FAMILY || family === 'all') && hasWebOption && (
            <button type="button" role="option" aria-selected={isWeb} className={`${styles.card} ${styles.cardCustom} ${isWeb ? styles.cardActive : ''}`} onClick={() => setSelected('plus-ppt')} disabled={busy}>
              <div className={styles.thumb}><div className={styles.customThumb}>▶</div></div>
              <div className={styles.meta}>
                <div className={styles.nameRow}><span className={styles.name}>{zh ? '网页版(plus-ppt)' : 'Web deck (plus-ppt)'}</span></div>
                <div className={styles.mood}>{zh ? '在线预览 / 动效 / 12 套主题' : 'Live preview, motion, 12 themes'}</div>
              </div>
            </button>
          )}
        </div>
        {isCustom && (
          <textarea className={styles.customInput} value={customText} disabled={busy} rows={4}
            placeholder={zh ? '例如:白底黑金,衬线标题,每页仅用金色标一个数据点;发丝线分隔;表格三线;禁止渐变与圆角…(≥10 字)' : 'e.g. white background, black & gold, serif headings, one gold data point per page, hairline rules, no gradients…'}
            onChange={e => setCustomText(e.target.value)} />
        )}
        <div className={styles.controls}>
          <div className={styles.control}>
            <span className={styles.controlLabel}>{zh ? '密度' : 'Density'}</span>
            <div className={styles.segmented} role="radiogroup">
              {catalog.densities.map(d => (
                <button key={d.id} type="button" role="radio" aria-checked={effectiveDensity === d.id} className={styles.segment} disabled={busy || isWeb} onClick={() => setDensity(d.id)}>{d.name}</button>
              ))}
            </div>
          </div>
          <div className={styles.control}>
            <span className={styles.controlLabel}>{zh ? '品牌色' : 'Brand'}</span>
            <input type="color" className={styles.colorInput} value={isValidHex(brand) ? hex(brand) : hex(current?.tokens.accent ?? '2251FF')} disabled={busy || isWeb} onChange={e => setBrand(e.target.value.toUpperCase())} aria-label={zh ? '品牌色' : 'Brand color'} />
            <input type="text" className={styles.hexInput} value={brand} placeholder={zh ? '#可选' : '#optional'} disabled={busy || isWeb} onChange={e => setBrand(e.target.value)} aria-label={zh ? '品牌色十六进制' : 'Brand hex'} />
            {brand && <button type="button" className={styles.clear} onClick={() => setBrand('')} disabled={busy}>{zh ? '清除' : 'Clear'}</button>}
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        {error && <div role="alert" className={styles.error}>{error}</div>}
        <div className={styles.summary}>
          {isWeb ? (zh ? '将改用 plus-ppt 生成网页版' : 'Will switch to plus-ppt') : isCustom ? (zh ? '自定义风格' : 'Custom style') : current ? `${current.id} ${current.name} · ${densityName(effectiveDensity)}${brand && isValidHex(brand) ? ` · ${hex(brand).toUpperCase()}` : ''}` : ''}
        </div>
        <button type="button" className={styles.deny} disabled={busy} onClick={() => { void submit(true); }}>{zh ? '拒绝此请求' : 'Decline'}</button>
        <button className={styles.submit} type="button" disabled={busy || !canSubmit} onClick={() => { void submit(); }}>{zh ? '使用此风格' : 'Use this style'}</button>
      </div>
    </div>
  );
};

export default PptxStylePicker;
